import { briefTextToHtml } from "@nlc/shared-types";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/button";
import { TiptapEditor } from "@/components/ui/TiptapEditor";

import { briefTextFromDoc } from "../brief-text";

// Each save re-runs the brief judge, so wait for a pause in typing.
const AUTOSAVE_DELAY_MS = 1000;

type BriefTextEditorProps = {
  /** A save of the brief is in flight. */
  isSaving?: boolean;
  onDone: () => void;
  /** Called with the edited plain text, only when it changed. */
  onSave: (value: string) => void;
  placeholder?: string;
  value: string;
};

export function BriefTextEditor({
  isSaving = false,
  onDone,
  onSave,
  placeholder,
  value,
}: BriefTextEditorProps) {
  // `content`, not `value`: TiptapEditor re-applies `value` whenever the
  // editor is empty, which would undo clearing the field.
  const initialContent = useMemo(() => briefTextToHtml(value), [value]);
  const draft = useRef<null | string>(null);
  const lastSaved = useRef(value.trim());
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [hasPendingEdit, setHasPendingEdit] = useState(false);

  const flush = () => {
    clearTimeout(timer.current);
    setHasPendingEdit(false);
    if (draft.current === null || draft.current === lastSaved.current) return;
    lastSaved.current = draft.current;
    onSave(draft.current);
  };
  const flushRef = useRef(flush);

  flushRef.current = flush;

  useEffect(() => () => flushRef.current(), []);

  return (
    <div className="flex flex-col gap-3">
      <TiptapEditor
        autofocus="end"
        className="max-h-[500px] max-w-full @container"
        content={initialContent}
        isMinimal
        // Blur fires before a click elsewhere, so Submit sees the latest text.
        onBlur={flush}
        onUpdate={({ editor }) => {
          draft.current = briefTextFromDoc(editor.getJSON());
          clearTimeout(timer.current);
          setHasPendingEdit(true);
          timer.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
        }}
        placeholder={placeholder}
      />
      <div className="flex items-center justify-end gap-3">
        <span className="flex items-center gap-1.5 text-xs text-text-tertiary">
          {isSaving || hasPendingEdit ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Saving...
            </>
          ) : (
            "Changes are saved automatically"
          )}
        </span>
        <Button
          onPress={() => {
            flush();
            onDone();
          }}
          size="sm"
          variant="secondary"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
